import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Button, Input, Modal, Popover } from 'antd';
import { LuX } from 'react-icons/lu';

/**
 * The correction toolbar on the player (design: corrections_design.html, section 2), shown after "Correct
 * detections": Add · click (A), Draw a box (B), Remove (R), then the assistant's two tools with their price in credits,
 * the whole-video check, the balance, and Done (Esc, back to watching). A line under it says what the tool does.
 */

const tk = ({ theme: t }) => t.token;

const Bar = styled.div`
	position: absolute;
	left: ${p => tk(p).marginSM}px;
	top: ${p => tk(p).marginSM}px;
	right: ${p => tk(p).marginSM}px;
	z-index: 5;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${p => tk(p).marginXXS}px;
	pointer-events: none;
`;

const Tools = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => tk(p).marginXXS}px;
	padding: ${p => tk(p).paddingXXS}px;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	pointer-events: auto;
`;

const Tool = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingSM}px`};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} transparent`};
	border-radius: ${p => tk(p).borderRadius}px;
	background: transparent;
	color: ${p => tk(p).colorTextSecondary};
	font-size: ${p => tk(p).fontSizeSM}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	white-space: nowrap;
	cursor: pointer;
	&:hover {
		border-color: ${p => tk(p).colorBorder};
	}
	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
	kbd {
		font-size: ${p => tk(p).fontSizeXXS}px;
		color: ${p => tk(p).colorTextTertiary};
		border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
		border-radius: ${p => tk(p).borderRadiusXS}px;
		padding: 0 ${p => tk(p).paddingXXS}px;
	}
	.cr {
		color: ${p => tk(p).colorPrimaryTextHover};
		font-size: ${p => tk(p).fontSizeXS}px;
	}
	${p =>
		p.$on &&
		css`
			background: ${tk(p).colorPrimary};
			border-color: ${tk(p).colorPrimary};
			color: ${tk(p).colorTextLightSolid};
			kbd,
			.cr {
				color: ${tk(p).colorTextLightSolid};
				border-color: ${tk(p).colorTextLightSolid};
			}
		`}
`;

const Sep = styled.span`
	align-self: stretch;
	width: ${p => tk(p).lineWidth}px;
	margin: ${p => `${tk(p).marginXXS}px ${tk(p).marginXXS}px`};
	background: ${p => tk(p).colorBorder};
`;

const Hint = styled.div`
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingSM}px`};
	border-radius: ${p => tk(p).borderRadius}px;
	background: ${p => tk(p).panelGlassBg};
	color: ${p => tk(p).colorText};
	font-size: ${p => tk(p).fontSizeXS}px;
	pointer-events: auto;
`;

const HintBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
	width: ${p => tk(p).controlHeight * 9}px;
`;

const errorText = err =>
	err?.response?.status === 402
		? 'Not enough credits'
		: err?.response?.data?.detail ||
			'The assistant could not look at this frame. You were not charged.';

const credits = n => (n == null ? '' : `${n} credit${n === 1 ? '' : 's'}`);

const TOOL_HINT = {
	click: s => `Click a ${s} that has no dot`,
	box: s => `Drag a box around a ${s} that has no dot`,
	remove: s => `Click a dot that is not a ${s}, or two dots that are one ${s}`,
};

const CorrectionToolbar = ({
	subject,
	tool,
	onTool,
	onDone,
	price,
	busy,
	onLookAgain,
	onHint,
	check = null,
	onPlanCheck = null,
	onStartCheck = null,
}) => {
	const [said, setSaid] = useState('');
	const [hintOpen, setHintOpen] = useState(false);
	const [text, setText] = useState('');
	const run = async (kind, action, ...args) => {
		setSaid('');
		try {
			const out = await action(...args);
			const misses = (out?.proposals || []).filter(p => p.type === 'add').length;
			setSaid(
				kind === 'hint'
					? misses
						? `Found it · ${credits(out.credits)} · accept it in the panel`
						: `Could not find it · ${credits(out.credits)}`
					: `Kept ${out.kept} · ${out.rejected} to take out · ${misses} missed · ${credits(out.credits)} · review in the panel`
			);
		} catch (err) {
			setSaid(errorText(err));
		}
	};
	const checking = check?.status === 'running';
	const askCheck = async () => {
		setSaid('');
		try {
			const plan = (await onPlanCheck())?.data;
			if (!plan?.frames) {
				setSaid('Nothing to check yet');
				return;
			}
			Modal.confirm({
				title: 'Check the whole video?',
				content: `The assistant looks at ${plan.frames} frames, so that it sees every counted ${subject} once: about ${credits(plan.credits)} (you have ${plan.balance}). Its proposals appear in the panel as it goes; it stops if the credits run out.`,
				okText: `Check · about ${credits(plan.credits)}`,
				onOk: async () => {
					try {
						await onStartCheck();
					} catch (err) {
						setSaid(errorText(err));
					}
				},
			});
		} catch (err) {
			setSaid(errorText(err));
		}
	};
	const line = checking
		? `Checking the video · ${check.done} of ${check.total} frames · ${credits(check.credits)}`
		: said || (tool ? TOOL_HINT[tool](subject) : null);
	return (
		<Bar onPointerDown={e => e.stopPropagation()}>
			<Tools role="toolbar" aria-label="Correct detections">
				<Tool type="button" $on={tool === 'click'} onClick={() => onTool('click')}>
					Add · click <kbd>A</kbd>
				</Tool>
				<Tool type="button" $on={tool === 'box'} onClick={() => onTool('box')}>
					Draw a box <kbd>B</kbd>
				</Tool>
				<Tool type="button" $on={tool === 'remove'} onClick={() => onTool('remove')}>
					Remove <kbd>R</kbd>
				</Tool>
				<Sep />
				<Tool type="button" disabled={busy} onClick={() => run('again', onLookAgain)}>
					Look again <span className="cr">{credits(price?.look_again)}</span>
				</Tool>
				<Popover
					open={hintOpen}
					onOpenChange={setHintOpen}
					trigger="click"
					content={
						<HintBox>
							<Input.TextArea
								autoFocus
								rows={2}
								maxLength={500}
								value={text}
								placeholder={`What did the count miss? e.g. "the ${subject} behind the fence"`}
								onChange={e => setText(e.target.value)}
							/>
							<Button
								type="primary"
								size="small"
								disabled={!text.trim() || busy}
								onClick={() => {
									setHintOpen(false);
									run('hint', onHint, text.trim());
								}}>
								Ask · {credits(price?.hint)}
							</Button>
						</HintBox>
					}>
					<Tool type="button" disabled={busy}>
						Tell it what it missed <span className="cr">{credits(price?.hint)}</span>
					</Tool>
				</Popover>
				{onPlanCheck && (
					<Tool type="button" disabled={busy || checking} onClick={askCheck}>
						Check the whole video
					</Tool>
				)}
				<Sep />
				{price && <Tool as="span">{price.balance} credits</Tool>}
				<Tool type="button" onClick={onDone} title="Back to watching (Esc)">
					<LuX /> Done <kbd>Esc</kbd>
				</Tool>
			</Tools>
			{line && <Hint>{line}</Hint>}
		</Bar>
	);
};

export default CorrectionToolbar;
